import React, { useEffect, useMemo, useState } from 'react';
import {
  Tag, Shield, AlertTriangle, Landmark, GraduationCap, PlayCircle, CalendarDays, MapPin, Package, Users,
  Building2, Stethoscope, Handshake, Wallet, Receipt, MessageCircle, MessageSquare, Search, ArrowRight, X,
} from 'lucide-react';
import { SECTIONS, FILTERS, SERVICES, TILES, TALK, WHO } from '../data/services';
import { setContactRole } from '../lib/leads';

// "Apply Here": one page that gets a visitor to the right application in as few taps as possible.
// 1) Big tiles for what most people need. 2) Three plain steps. 3) The full list, with search and a "who are you" choice.
// Not a shop: no cart, no checkout. Each button opens the right page, form or message.
const ICONS = {
  tag: Tag, shield: Shield, alert: AlertTriangle, landmark: Landmark, cap: GraduationCap, play: PlayCircle, calendar: CalendarDays,
  pin: MapPin, package: Package, users: Users, building: Building2, stethoscope: Stethoscope, handshake: Handshake,
  wallet: Wallet, receipt: Receipt, chat: MessageCircle, message: MessageSquare,
};

const put = (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } };
const BY_ID = Object.fromEntries(SERVICES.map((s) => [s.id, s]));

export default function ServicesPage({ isDark, onNavigate }) {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bg = isDark ? 'bg-gray-950' : 'bg-white';
  const soft = isDark ? 'bg-gray-900' : 'bg-[#F4F7F2]';
  const head = isDark ? 'text-white' : 'text-[#14301A]';
  const body = isDark ? 'text-gray-300' : 'text-gray-600';
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';

  const run = (act) => {
    if (act.type === 'page') {
      if (act.form) put('imfuyo-open-form', act.form);
      if (act.scroll) put('imfuyo-scroll-to', act.scroll);
      onNavigate(act.page);
    } else if (act.type === 'scroll') {
      put('imfuyo-scroll-to', act.target);
      onNavigate(act.page);
    } else if (act.type === 'role') {
      setContactRole(act.role);
      onNavigate('landing', 'contact');
    } else if (act.type === 'contact') {
      onNavigate('landing', 'contact');
    } else if (act.type === 'mbpview') {
      put('imfuyo-mbp-view', act.view);
      onNavigate('mbuziplus');
    } else if (act.type === 'shop') {
      window.location.assign(act.path);
    } else if (act.type === 'href') {
      window.open(act.href, '_blank', 'noopener,noreferrer');
    }
  };

  const needle = q.trim().toLowerCase();
  const matches = useMemo(() => SERVICES.filter((s) => {
    const inFilter = filter === 'all' || (WHO[s.id] || [s.section]).includes(filter);
    if (!inFilter) return false;
    if (!needle) return true;
    return `${s.title} ${s.what} ${s.detail} ${s.keywords || ''}`.toLowerCase().includes(needle);
  }), [filter, needle]);

  const sections = SECTIONS.map((sec) => ({ ...sec, items: matches.filter((s) => s.section === sec.key) })).filter((sec) => sec.items.length);

  return (
    <div className={bg} style={{ fontFamily: "'Inter', system-ui, sans-serif", paddingTop: '80px' }}>
      {/* 1. What do you need? */}
      <section className="bg-[#14301A] text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-10 items-center">
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#B7D9A8] mb-3">Apply here</p>
            <h1 className="text-4xl sm:text-6xl font-bold leading-[1.05]">What do you need today?</h1>
            <p className="mt-4 text-xl text-white/85 max-w-xl">Tap one. We will take you to the right place.</p>
          </div>
          <div className="relative hidden lg:block h-[260px]">
            <img src="/partners/hero-goat.jpg" alt="A dairy goat looking out from a wooden pen" className="absolute right-0 top-0 w-[70%] h-full object-cover rounded-3xl shadow-2xl" style={{ objectPosition: '50% 35%' }} />
            <img src="/partners/step-sell.jpg" alt="Farmers leading young goats to a vehicle" loading="lazy" className="absolute left-0 bottom-[-18px] w-[52%] h-[48%] object-cover rounded-2xl border-4 border-[#14301A] shadow-xl" />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {TILES.map((t) => {
              const Icon = ICONS[t.icon] || Tag;
              const act = t.id === 'talk' ? TALK : BY_ID[t.id].act;
              return (
                <button key={t.id} onClick={() => run(act)}
                  className="group text-left rounded-3xl bg-white text-[#14301A] p-4 sm:p-5 min-h-[150px] sm:min-h-[164px] flex flex-col justify-between shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition focus:outline-none focus:ring-4 focus:ring-[#B7D9A8]">
                  <span className="w-14 h-14 rounded-2xl bg-[#EAF2E4] text-[#285020] flex items-center justify-center group-hover:bg-[#BF4700] group-hover:text-white transition-colors"><Icon className="w-7 h-7" aria-hidden="true" /></span>
                  <span className="flex items-end justify-between gap-2">
                    <span className="text-lg sm:text-xl font-bold leading-tight">{t.label}</span>
                    <ArrowRight className="w-5 h-5 shrink-0 text-[#BF4700] transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2. Three easy steps */}
      <section className={bg}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-6 ${head}`}>It is easy. Three steps.</h2>
          <ol className="grid gap-4 md:grid-cols-3">
            {[
              ['Tap what you need.', 'Pick a big button above, or look at the full list below.'],
              ['Fill in a short form.', 'Your name, your phone and a few details.'],
              ['We reply.', 'The Imfuyo team replies on WhatsApp or by phone, usually within one working day.'],
            ].map(([t, d], i) => (
              <li key={t} className={`rounded-2xl border p-5 flex gap-4 items-start ${card}`}>
                <span className="w-11 h-11 shrink-0 rounded-full bg-[#285020] text-white text-xl font-bold flex items-center justify-center">{i + 1}</span>
                <span><b className={`block text-lg ${head}`}>{t}</b><span className={body}>{d}</span></span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 3. Everything, with search */}
      <div className={soft} id="all-services">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className={`text-2xl sm:text-3xl font-bold ${head}`}>Or look at everything.</h2>
          <p className={`mt-2 mb-6 ${body}`}>Say who you are, or type a word like insurance, loan or course.</p>

          <div className="relative max-w-xl">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} type="search" aria-label="Search services"
              placeholder="Search: insurance, loan, course…"
              className="w-full h-14 rounded-full pl-12 pr-12 text-base text-gray-900 bg-white border border-gray-300 outline-none focus:ring-4 focus:ring-[#285020]/25"
            />
            {q && <button onClick={() => setQ('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"><X className="w-4 h-4" /></button>}
          </div>

          <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Who are you">
            {FILTERS.map((f) => (
              <button key={f.key} role="tab" aria-selected={filter === f.key} onClick={() => setFilter(f.key)}
                className={`px-5 h-12 rounded-full text-base font-semibold border transition-colors ${filter === f.key ? 'bg-[#285020] border-[#285020] text-white' : isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-[#14301A] bg-white hover:bg-[#EAF2E4]'}`}>
                {f.label}
              </button>
            ))}
          </div>

          <p className={`text-sm mt-6 mb-8 ${body}`} aria-live="polite">{matches.length} service{matches.length === 1 ? '' : 's'}{needle ? ` for "${q.trim()}"` : ''}</p>

          {sections.length === 0 && (
            <div className={`rounded-2xl border p-8 text-center ${card}`}>
              <h3 className={`text-xl font-bold mb-2 ${head}`}>We could not find that one.</h3>
              <p className={`mb-5 ${body}`}>Tell us what you need and we will help.</p>
              <button onClick={() => run({ type: 'contact' })} className="px-6 py-3 rounded-full bg-[#D55103] hover:bg-[#B84402] text-white font-semibold">Send us a message</button>
            </div>
          )}

          {sections.map((sec) => (
            <section key={sec.key} className="mb-12 last:mb-0" aria-labelledby={`svc-${sec.key}`}>
              <div className="relative mb-6 rounded-2xl overflow-hidden min-h-[130px] sm:min-h-[160px] flex items-end bg-[#14301A]">
                {sec.img && <img src={sec.img} alt={sec.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: sec.pos || '50% 50%' }} />}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(20,48,26,.92) 0%, rgba(20,48,26,.7) 45%, rgba(20,48,26,.05) 100%)' }} />
                <div className="relative p-5 sm:p-7 text-white max-w-xl">
                  <h3 id={`svc-${sec.key}`} className="text-2xl sm:text-3xl font-bold">{sec.title}</h3>
                  <p className="text-white/85 mt-1">{sec.text}</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sec.items.map((s) => {
                  const Icon = ICONS[s.icon] || Tag;
                  return (
                    <article key={s.id} className={`rounded-2xl border p-6 flex flex-col transition-shadow hover:shadow-lg ${card}`}>
                      <span className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-[#1d2e1f] text-[#B7D9A8]' : 'bg-[#EAF2E4] text-[#285020]'}`}><Icon className="w-6 h-6" aria-hidden="true" /></span>
                      <h4 className={`text-xl font-bold mb-1.5 ${head}`}>{s.title}</h4>
                      <p className={`text-base leading-relaxed mb-2 ${body}`}>{s.what}</p>
                      <p className={`text-sm font-semibold mb-5 ${isDark ? 'text-[#B7D9A8]' : 'text-[#285020]'}`}>{s.detail}</p>
                      <button onClick={() => run(s.act)} className="mt-auto inline-flex items-center justify-center gap-2 px-5 h-12 rounded-full bg-[#285020] hover:bg-[#1d3c18] text-white text-base font-bold">
                        {s.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <p className={`mt-12 text-sm ${body}`}>
            Want to buy ear tags, tools or feed? Visit the{' '}
            <button onClick={() => onNavigate('shop')} className="underline font-semibold text-[#285020]">Market Place</button>.
          </p>
        </div>
      </div>

      <section className="bg-[#285020] text-white">
        <div className="max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold">Still not sure?</h2>
          <p className="mt-3 text-lg text-white/85">Talk to a real person. We will help you choose.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button onClick={() => run(TALK)} className="px-7 h-14 rounded-full bg-[#D55103] hover:bg-[#B84402] text-lg font-bold">Message us on WhatsApp</button>
            <button onClick={() => run({ type: 'contact' })} className="px-7 h-14 rounded-full border border-white/50 text-lg font-semibold hover:bg-white/10">Send us a message</button>
          </div>
        </div>
      </section>
    </div>
  );
}
